import { isDefined, isNotDefined } from '@sgme/fp';
import { Spacer, VStack } from '@sgme/ui';
import deepEqual from 'fast-deep-equal';
import { memo, useCallback } from 'react';

import { sendHelpRequestThunk } from '@/api/help-request/index.ts';
import type { SendHelpRequestRequest } from '@/api/help-request/sendHelpRequest.ts';
import { useTrackEvent } from '@/App/Blotter/useTrackEvent.ts';
import { OrderClientWay } from '@/App/tiles/orders/components/OrderClientWay.tsx';
import { OrderFooter } from '@/App/tiles/orders/components/OrderFooter.tsx';
import { OrderHeader } from '@/App/tiles/orders/components/OrderHeader.tsx';
import { OrderNotifications } from '@/App/tiles/orders/components/OrderNotifications.tsx';
import { OrderNotional } from '@/App/tiles/orders/components/OrderNotional.tsx';
import { ConnectedStopLossOrderSpot } from '@/App/tiles/orders/StopLossOrder/ConnectedStopLossOrderSpot.tsx';
import {
  useStopLossIsChecking,
  useStopLossIsModifyingField,
  useStopLossSetIsModifyingField,
  useStopLossTileId,
  useValidateStopLoss,
} from '@/App/tiles/orders/StopLossOrder/StopLossOrderCreationTile/stopLossContext';
import { StopLossExpiry } from '@/App/tiles/orders/StopLossOrder/StopLossOrderCreationTile/StopLossExpiry.tsx';
import { StopLossLimitPrice } from '@/App/tiles/orders/StopLossOrder/StopLossOrderCreationTile/StopLossLimitPrice.tsx';
import { StopLossTrigger } from '@/App/tiles/orders/StopLossOrder/StopLossOrderCreationTile/StopLossTrigger.tsx';
import { AutoAdvanceForm } from '@/components/AutoAdvanceForm.tsx';
import { logger } from '@/components/SgwtBootstrap/logger.ts';
import { getConfig } from '@/config/config.ts';
import { useAppDispatch, useAppSelector } from '@/store/hooks.ts';
import type { ValidationResponseDetailContent } from '@/store/orders/state.ts';
import { useSubmitStopLossMutation } from '@/store/orders/stopLoss/api/index.ts';
import {
  selectClientWayStopLossData,
  selectNotificationEmailsStopLoss,
  selectNotionalDataStopLoss,
  selectStopLossErrors,
  selectStopLossTile,
} from '@/store/orders/stopLoss/selectors.ts';
import { selectTileClientId } from '@/store/tiles/selectors.ts';

export const StopLossOrderCreationTile = memo(() => {
  const dispatch = useAppDispatch();
  const track = useTrackEvent();
  const tileId = useStopLossTileId();
  const setIsModifyingField = useStopLossSetIsModifyingField();
  const isModifyingField = useStopLossIsModifyingField();
  const notificationEmails = useAppSelector((state) => selectNotificationEmailsStopLoss(state, tileId));
  const notionalData = useAppSelector((state) => selectNotionalDataStopLoss(state, tileId));
  const clientWayData = useAppSelector((state) => selectClientWayStopLossData(state, tileId));
  const clientId = useAppSelector((state) => selectTileClientId(state, tileId));

  const errors = useAppSelector((state) => selectStopLossErrors(state, tileId));
  const validate = useValidateStopLoss();
  const [submitStopLoss] = useSubmitStopLossMutation({
    selectFromResult: () => ({}),
  });

  const isChecking = useStopLossIsChecking();

  const { areFieldsVisible, tile } = useAppSelector((state) => {
    const tile = selectStopLossTile(state, tileId);

    if (isNotDefined(tile)) {
      return {
        areFieldsVisible: false,
      };
    }

    return {
      tile,
      areFieldsVisible: isDefined(tile.properties.currencyPair),
    };
  }, deepEqual);

  const handleSubmit = useCallback(() => {
    track('Order action', 'Submit Order');
    if (isNotDefined(tile)) {
      console.error(`[handleSubmit] Tile with id ${tileId} not found in state.`);
      return;
    }

    if (isNotDefined(clientId)) {
      console.error('[handleSubmit] client id is not defined');
      return;
    }

    if (isNotDefined(notificationEmails)) {
      console.error('[handleSubmit] Notification emails not found in the tile or user preferences.');
      return;
    }

    if (
      isNotDefined(tile.properties.currencyPair) ||
      isNotDefined(tile.properties.notional) ||
      isNotDefined(tile.properties.limitPrice)
    ) {
      console.error('[handleSubmit] Currency pair is not defined in the tile.');
      return;
    }

    submitStopLoss({
      tileId,
      currency1: tile.properties.currencyPair.currency1,
      currency2: tile.properties.currencyPair.currency2,
      clientId,
      way: tile.properties.clientWay,
      notional: tile.properties.notional,
      notionalCurrencyIndex: tile.properties.notionalCurrencyIndex,
      limitPrice: tile.properties.limitPrice,
      expiry: tile.properties.expiry,
      notificationEmails: tile.properties.notificationEmails ?? [],
      trigger: tile.properties.trigger,
      clockOffset: tile.properties.clockOffset,
    })
      .unwrap()
      .catch(({ errors }: { type: 'failure'; errors: ValidationResponseDetailContent[] }) => {
        console.error('Failed to submit take profit order:', errors);
        if (errors.some((error) => error.errorDescription === 'timeout')) {
          const params: SendHelpRequestRequest = {
            config: 'orderpending',
            details: JSON.stringify({
              payload: errors,
              orderIdToUpdate: tileId,
            }),
            data: {
              orderDescription: 'creation',
              orderType: 'limit',
              /**
               * send 'g10' or 'emerging' on order creation when we are able to differentiate
               */
              orderCurrencyType: 'unknown',
              orderId: tileId,
            },
            templateId: getConfig().app.mailTemplateIds.templateIdForPendingOrder,
            templateIdConfirmation: getConfig().app.mailTemplateIds.templateIdForPendingOrderConfirmation,
          };

          logger.logError('Order submission failed on timeout for {tileId_s}', tileId);
          dispatch(sendHelpRequestThunk(params));
        }
      });
  }, [tileId, clientId, notificationEmails, tile, submitStopLoss]);

  return (
    <AutoAdvanceForm className="d-flex w-100 h-100">
      <VStack className="bg-lvl1 py-2" fullWidth fullHeight>
        <OrderHeader orderType="StopLossOrder" tile={tile} tileId={tileId} validate={validate} />

        {areFieldsVisible && (
          <>
            <div
              className="mx-16px"
              style={{
                display: 'grid',
                gap: '5px',
                gridTemplateColumns: 'auto minmax(0, 1fr)',
                gridTemplateRows: '20px',
                alignItems: 'center',
                overflow: 'hidden',
              }}
            >
              <ConnectedStopLossOrderSpot />
              <OrderClientWay clientWayData={clientWayData} validate={validate} />
              <OrderNotional
                errors={errors}
                notionalData={notionalData}
                validate={validate}
                setIsModifyingField={setIsModifyingField}
              />
              <StopLossLimitPrice />
              <StopLossExpiry />
              <StopLossTrigger />
              <OrderNotifications
                tileId={tileId}
                errors={errors}
                notificationEmails={notificationEmails}
                validate={validate}
              />
            </div>

            <Spacer />

            <OrderFooter tile={tile} isModifyingField={isModifyingField} handleSubmit={handleSubmit} />
          </>
        )}
      </VStack>
      {isChecking && (
        <div className="position-absolute z-3 top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-lvl1 bg-opacity-75">
          <div className="spinner blinker-lg" />
        </div>
      )}
    </AutoAdvanceForm>
  );
});
